import React, { useEffect, useRef, useState } from "react";
import FilterIcon from "../../../../Assets/ActionIcons/FilterIcon.svg";
import CloseIcon from "../../../../Assets/ActionIcons/CloseIcon.svg";

export interface IntegratedCustomerListFilterValue {
  creationMethod:
    | "all"
    | "autoMapped"
    | "autoCreated"
    | "mappedManually"
    | "imported"
    | "fromZomentumGrow";
  createdDateStart: Date | null;
  createdDateEnd: Date | null;
}

interface IntegratedCustomerListFilterProps {
  value: IntegratedCustomerListFilterValue;
  onChange: (value: IntegratedCustomerListFilterValue) => void;
}

const defaultFilterValue: IntegratedCustomerListFilterValue = {
  creationMethod: "all",
  createdDateStart: null,
  createdDateEnd: null
};

const IntegratedCustomerListFilter: React.FC<IntegratedCustomerListFilterProps> = ({
  value,
  onChange
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [pending, setPending] = useState<IntegratedCustomerListFilterValue>(value);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPending(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const apply = () => {
    onChange(pending);
    setOpen(false);
  };

  const reset = () => {
    setPending(defaultFilterValue);
    onChange(defaultFilterValue);
    setOpen(false);
  };

  const appliedFilters: {
    field: keyof IntegratedCustomerListFilterValue;
    label: string;
  }[] = [];
  if (value.creationMethod !== "all") {
    const methodLabel =
      value.creationMethod === "autoMapped"
        ? "Automatically mapped"
        : value.creationMethod === "autoCreated"
          ? "Automatically created"
          : value.creationMethod === "mappedManually"
            ? "Mapped manually"
            : value.creationMethod === "imported"
              ? "Imported"
              : "From Zomentum Grow";
    appliedFilters.push({ field: "creationMethod", label: methodLabel });
  }
  if (value.createdDateStart) {
    const from = value.createdDateStart.toISOString().split("T")[0];
    appliedFilters.push({
      field: "createdDateStart",
      label: `Create Date From: ${from}`
    });
  }
  if (value.createdDateEnd) {
    const to = value.createdDateEnd.toISOString().split("T")[0];
    appliedFilters.push({
      field: "createdDateEnd",
      label: `Create Date To: ${to}`
    });
  }

  const removeFilter = (field: keyof IntegratedCustomerListFilterValue) => {
    const updated = { ...value };
    if (field === "creationMethod") {
      updated.creationMethod = defaultFilterValue.creationMethod;
    } else if (field === "createdDateStart") {
      updated.createdDateStart = defaultFilterValue.createdDateStart;
    } else if (field === "createdDateEnd") {
      updated.createdDateEnd = defaultFilterValue.createdDateEnd;
    }
    setPending(updated);
    onChange(updated);
  };

  return (
    <div className="relative flex items-center space-x-2">
      <button
        className="p-2 border rounded-lg border-gray-300 hover:bg-gray-200 transition-all duration-500"
        onClick={() => setOpen(true)}
      >
        <img src={FilterIcon} alt="filter" />
      </button>
      {appliedFilters.length > 0 && (
        <div className="flex space-x-2 items-center">
          {appliedFilters.map(filter => (
            <div
              key={filter.field}
              className="bg-gray-100 px-2 py-1 rounded-lg flex items-center space-x-1"
            >
              <span>{filter.label}</span>
              <button
                className="rounded-full hover:bg-gray-200 flex items-center justify-center p-1"
                onClick={() => removeFilter(filter.field)}
              >
                <img src={CloseIcon} alt="close" className="h-2 w-2" />
              </button>
            </div>
          ))}
        </div>
      )}
      {open && (
        <div
          ref={menuRef}
          className="absolute top-full left-0 w-64 bg-white rounded shadow z-20 p-3 mt-1"
        >
          <div className="flex flex-col mb-3">
            <label className="font-semibold">Creation Method</label>
            <select
              className="p-2 border border-gray-200 rounded"
              value={pending.creationMethod}
              onChange={e =>
                setPending({
                  ...pending,
                  creationMethod: e.target
                    .value as IntegratedCustomerListFilterValue["creationMethod"]
                })
              }
            >
              <option value="all">All</option>
              <option value="autoMapped">Automatically mapped</option>
              <option value="autoCreated">Automatically created</option>
              <option value="mappedManually">Mapped manually</option>
              <option value="imported">Imported</option>
              <option value="fromZomentumGrow">From Zomentum Grow</option>
            </select>
          </div>
          <div className="flex flex-col mb-3">
            <label className="mb-2 font-semibold">Create Date</label>
            <div className="grid grid-cols-3 gap-2">
              <label className="col-span-1">Start</label>
              <input
                type="date"
                className="col-span-2 px-2 py-1 border border-gray-200 rounded"
                value={pending.createdDateStart?.toISOString().split("T")[0] ?? ""}
                onChange={e =>
                  setPending({
                    ...pending,
                    createdDateStart: e.target.value ? new Date(e.target.value) : null
                  })
                }
              />
              <label className="col-span-1">End</label>
              <input
                type="date"
                className="col-span-2 px-2 py-1 border border-gray-200 rounded"
                value={pending.createdDateEnd?.toISOString().split("T")[0] ?? ""}
                onChange={e =>
                  setPending({
                    ...pending,
                    createdDateEnd: e.target.value ? new Date(e.target.value) : null
                  })
                }
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <button
              className="rounded bg-blue-600 hover:bg-blue-500 text-white p-2"
              onClick={apply}
            >
              Apply
            </button>
            <button
              className="rounded border border-gray-200 p-2 hover:bg-gray-100"
              onClick={reset}
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegratedCustomerListFilter;
